/**
 * 日次集計スナップショット(daily_summary)と月次目標(targets)の読み取り。
 * 書き込みは Phase 2 の日次集計エンジン(CSV取込→集計)が行う。
 */
import { collection, doc, documentId, getDoc, getDocs, limit, orderBy, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { writeAuditLog } from '@core/lib/auditLog';
import { DEFAULT_MONTH_TARGETS, type DailySummary, type DateStr, type MonthStr, type MonthTargets } from '@core/lib/types';

export async function fetchDailySummary(date: DateStr): Promise<DailySummary | null> {
  const snap = await getDoc(doc(getDb(), 'daily_summary', date));
  if (!snap.exists()) return null;
  return { id: snap.id, ...(snap.data() as object) } as DailySummary;
}

/** 集計データがある最新日(初期表示に使う) */
export async function fetchLatestSummaryDate(): Promise<DateStr | null> {
  const snap = await getDocs(query(collection(getDb(), 'daily_summary'), orderBy(documentId(), 'desc'), limit(1)));
  return snap.docs[0]?.id ?? null;
}

/** from〜to(両端含む)の日次集計 */
export async function fetchSummariesRange(from: DateStr, to: DateStr): Promise<DailySummary[]> {
  const snap = await getDocs(
    query(collection(getDb(), 'daily_summary'), where(documentId(), '>=', from), where(documentId(), '<=', to), orderBy(documentId())),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as DailySummary);
}

export async function fetchMonthSummaries(month: MonthStr): Promise<DailySummary[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), 'daily_summary'),
      where(documentId(), '>=', `${month}-01`),
      where(documentId(), '<=', `${month}-31`),
      orderBy(documentId()),
    ),
  );
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as object) }) as DailySummary);
}

export async function fetchMonthTargets(month: MonthStr): Promise<MonthTargets> {
  const snap = await getDoc(doc(getDb(), 'targets', month));
  const saved = (snap.data() ?? {}) as Partial<MonthTargets>;
  return {
    id: month,
    ...DEFAULT_MONTH_TARGETS,
    ...saved,
    fallback_unit_price: { ...DEFAULT_MONTH_TARGETS.fallback_unit_price, ...(saved.fallback_unit_price ?? {}) },
    manager_targets: saved.manager_targets ?? {},
    expenses: saved.expenses ?? [],
    company_targets: saved.company_targets ?? {},
    manager_bonus_rates: saved.manager_bonus_rates ?? DEFAULT_MONTH_TARGETS.manager_bonus_rates,
    timee_fee_per_person: saved.timee_fee_per_person ?? DEFAULT_MONTH_TARGETS.timee_fee_per_person,
  };
}

/** 月次設定(目標・キャンセル率・通話料単価・経費・会社別目標)の保存 */
export async function saveMonthTargets(month: MonthStr, patch: Partial<Omit<MonthTargets, 'id'>>, userId: string): Promise<void> {
  const { id: _id, ...rest } = patch as MonthTargets;
  await setDoc(doc(getDb(), 'targets', month), { ...rest, updated_by: userId, updated_at: serverTimestamp() }, { merge: true });
  await writeAuditLog({ targetType: 'import', targetId: `targets/${month}`, action: 'targets-update', by: userId, changes: ['月次設定を更新'] });
}
